import { useQuery } from "@tanstack/react-query"
import { Link, useParams } from "@tanstack/react-router"
import { type Edge, MarkerType } from "@xyflow/react"
import { FileQuestion, GitBranch, History, RotateCw } from "lucide-react"
import { useMemo, useState } from "react"
import { Mono } from "@/components/copy-button"
import { GraphCanvas, recordedEdge, TruthBadge } from "@/components/graph/canvas"
import { WHY_NODE_HEIGHT, WHY_NODE_WIDTH, type WhyFlowNode, WhyNodeView } from "@/components/graph/nodes"
import { OpenInGraph } from "@/components/graph/open-in-graph"
import { JsonView } from "@/components/json-view"
import { NeighborhoodPanel } from "@/components/neighborhood"
import { PageHeader } from "@/components/shell/page-header"
import { EmptyState, ErrorState, TableSkeleton } from "@/components/states"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { PageHeader as ContentHeader } from "@/components/ui/page-header"
import { Screen } from "@/components/ui/screen"
import { Section } from "@/components/ui/section"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Value } from "@/components/value"
import { HiddenChip } from "@/workspace/values"
import { refusalOfError, type Schemas } from "@/lib/api"
import { attributeField, type FieldAttribution } from "@/lib/attribution"
import { useFormatNumber, useT } from "@/lib/i18n"
import { layeredLayout } from "@/lib/layout"
import { stringifyExact } from "@/lib/lossless"
import { useDefinitions, useObject, useProvenance, useWhy } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { asArray, asString, isRecord } from "@/lib/utils"
import { connectedEdges, type WhyResponse, type WhyStep } from "@/lib/why"
import { objectTypes } from "./objects"

export function ObjectDetailPage() {
  const t = useT()
  const formatNumber = useFormatNumber()
  const scope = useScope()
  const { objectType, key } = useParams({ from: "/$orgId/$environmentId/objects/$objectType/$key" })
  const object = useObject(scope, objectType, key)
  const defs = useDefinitions(scope)
  // Browsing a parent or interface type returns subtype instances: label and order by the concrete type served.
  const concreteType = object.data?.objectType ?? objectType
  const order = useMemo(
    () => (defs.data ? (objectTypes(defs.data).find((t) => t.name === concreteType)?.properties ?? []) : []),
    [defs.data, concreteType],
  )
  const values = (object.data?.values ?? {}) as Record<string, unknown>
  const masked = object.data?.masked as Record<string, unknown> | undefined
  const keysInOrder = [...order.filter((p) => p in values), ...Object.keys(values).filter((p) => !order.includes(p))]
  const [tab, setTab] = useState("why")
  const provenance = useProvenance(scope, objectType, key, tab === "values" && object.isSuccess)
  const why = useWhy(scope, objectType, key)
  // Evidence belongs to one object version; it is shown only against that version.
  const evidenceVersion = provenance.data?.version
  const current = provenance.isSuccess && object.data !== undefined && evidenceVersion === object.data.version
  const evidence = useMemo(
    () => new Map((provenance.data?.properties ?? []).map((p) => [p.property, p.evidence])),
    [provenance.data],
  )
  const refresh = () =>
    void Promise.all([object.refetch(), ...(provenance.data || provenance.isError ? [provenance.refetch()] : [])])

  return (
    <>
      <PageHeader
        crumbs={[
          <Link key="o" to="/$orgId/$environmentId/objects" params={scope}>
            {t("nav.objects")}
          </Link>,
          <Link key="t" to="/$orgId/$environmentId/objects/$objectType" params={{ ...scope, objectType }}>
            {objectType}
          </Link>,
          key,
        ]}
        actions={
          <Button
            size="icon"
            variant="ghost"
            onClick={refresh}
            disabled={object.isFetching || provenance.isFetching}
            aria-label={t("common.refresh")}
          >
            <RotateCw className={object.isFetching || provenance.isFetching ? "animate-spin" : undefined} />
          </Button>
        }
      />
      <Screen width="full" className="flex min-h-0 flex-1 flex-col space-y-0 px-4 py-6 md:px-6">
        {object.isPending ? (
          <TableSkeleton rows={6} columns={2} />
        ) : object.isError ? (
          <ErrorState error={object.error} what={`${objectType} ${key}`} onRetry={() => void object.refetch()} />
        ) : (
          <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
            <div>
              <ContentHeader
                actions={<OpenInGraph object={{ objectType: concreteType, key }} />}
                identity={
                  <>
                    <Mono value={key} className="[&_code]:text-base [&_code]:font-medium" />
                    <Badge tone="outline">{concreteType}</Badge>
                    {concreteType !== objectType ? (
                      <span className="text-xs text-muted-foreground">{t("objects.readAs", { name: objectType })}</span>
                    ) : null}
                    <span className="font-mono text-xs text-muted-foreground tabular">v{object.data.version}</span>
                    {object.data.observedDeleted ? <Badge tone="danger">{t("objects.deletedInSource")}</Badge> : null}
                  </>
                }
              />
              <TabsList className="mt-3">
                <TabsTrigger value="why">{t("objects.why")}</TabsTrigger>
                <TabsTrigger value="values">
                  {t("objects.values")}{" "}
                  <span className="text-xs text-subtle-foreground tabular">{formatNumber(keysInOrder.length)}</span>
                </TabsTrigger>
                <TabsTrigger value="links">{t("definitionSummary.links")}</TabsTrigger>
                <TabsTrigger value="json">{t("objects.json")}</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="why" className="min-h-[32rem] flex-1 pt-6">
              <WhyPanel objectType={objectType} objectKey={key} />
            </TabsContent>
            <TabsContent value="values" className="overflow-y-auto pt-6">
              {provenance.isError ? (
                <p role="alert" className="mb-3 max-w-3xl text-xs text-destructive">
                  {t("objects.evidenceReadFailed")} <ErrorText error={provenance.error} />
                </p>
              ) : provenance.isSuccess && !current ? (
                <p
                  role="status"
                  className="mb-3 max-w-3xl rounded-md border border-border-subtle bg-card px-3 py-2 text-xs text-muted-foreground"
                >
                  {t("objects.evidenceVersionMismatch", {
                    evidenceVersion: evidenceVersion ?? "",
                    objectVersion: object.data.version,
                  })}{" "}
                  <button type="button" className="underline hover:text-foreground" onClick={refresh}>
                    {t("objects.readBothAgain")}
                  </button>
                </p>
              ) : provenance.data?.historyTruncated ? (
                <p className="mb-3 max-w-3xl text-xs text-muted-foreground">
                  {t("objects.partialHistory")}
                </p>
              ) : null}
              <Section>
                <dl className="max-w-3xl divide-y divide-border rounded-lg border border-border bg-card px-5 py-2">
                  {keysInOrder.map((p) => (
                    <div key={p} className="grid gap-1 py-2 md:grid-cols-[200px_1fr] md:gap-4">
                      <dt>
                        <code className="font-mono text-xs text-muted-foreground">{p}</code>
                      </dt>
                      <dd className="min-w-0 text-sm [overflow-wrap:anywhere]">
                        {masked && p in masked ? <HiddenChip /> : <Value value={values[p]} />}
                        <FieldSource
                          pending={(provenance.isPending || provenance.isFetching) && tab === "values"}
                          known={current}
                          evidence={evidence.get(p)}
                          attribution={why.data ? attributeField(p, values[p], object.data.version, why.data, provenance.data) : undefined}
                        />
                      </dd>
                    </div>
                  ))}
                </dl>
              </Section>
              {object.data.deletedLinkTargets?.length ? (
                <p className="mt-4 text-xs text-warning">
                  {t("objects.deletedLinkTargets", { count: object.data.deletedLinkTargets.length })}
                </p>
              ) : null}
            </TabsContent>
            <TabsContent value="links" className="min-h-[32rem] flex-1 pt-6">
              <NeighborhoodPanel key={`${concreteType}/${key}`} root={{ objectType: concreteType, key }} />
            </TabsContent>
            <TabsContent value="json" className="overflow-y-auto pt-6">
              <JsonView value={object.data} />
            </TabsContent>
          </Tabs>
        )}
      </Screen>
    </>
  )
}

type FieldEvidence = Schemas["FieldEvidence"]

/** Where a field's current value was recorded from: ledger evidence only, never inferred from mappings. */
function FieldSource({
  pending,
  known,
  evidence,
  attribution,
}: {
  pending: boolean
  known: boolean
  evidence: readonly FieldEvidence[] | undefined
  attribution?: FieldAttribution
}) {
  const t = useT()
  if (pending) return <Skeleton className="mt-1.5 h-3 w-48" />
  if (!known) return null
  if (!evidence?.length) {
    if (attribution?.kind === "action")
      return (
        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-2xs text-muted-foreground">
          <History className="size-3 text-subtle-foreground" aria-hidden />
          {t("objects.writtenByAction", { action: attribution.step.actionId ?? "", actor: attribution.step.actor ?? "", commit: attribution.step.commit })}
        </p>
      )
    return <p className="mt-1 text-2xs text-subtle-foreground">{t(attribution?.kind === "hidden" ? "objects.historyHidden" : "objects.noRecordedSource")}</p>
  }
  return (
    <ul className="mt-1 flex flex-col gap-0.5">
      {evidence.map((e) => (
        <li
          key={`${e.commit}:${e.source.system}:${e.source.record}`}
          className="flex flex-wrap items-center gap-x-1.5 text-2xs text-muted-foreground"
        >
          <History className="size-3 text-subtle-foreground" aria-hidden />
          <span>
            {e.source.system} <code className="font-mono">{e.source.record}</code>
          </span>
          <span className="text-subtle-foreground">{t("objects.via")}</span>
          <code className="font-mono">{e.mappingVersion}</code>
          {e.transform ? <code className="font-mono text-subtle-foreground">{e.transform}</code> : null}
          <span
            className="text-subtle-foreground tabular"
            title={
              e.observedAt
                ? t("objects.recordedAndObservedAt", { recordedAt: e.recordedAt, observedAt: e.observedAt })
                : t("objects.recordedAt", { recordedAt: e.recordedAt })
            }
          >
            {t("objects.evidenceCommit", { commit: e.commit, version: e.version })}
          </span>
          {e.upstream.length ? (
            <span
              className="text-subtle-foreground"
              title={e.upstream.map((u) => `${u.objectType}/${u.key}@${u.version}`).join("\n")}
            >
              {t("objects.upstreamFacts", { count: e.upstream.length })}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

function ErrorText({ error }: { error: unknown }) {
  const refused = refusalOfError(error)
  return (
    <span>
      {refused
        ? `${refused.refusal.message} (${refused.refusal.code})`
        : error instanceof Error
          ? error.message
          : String(error)}
    </span>
  )
}

function whyFlow(
  answer: WhyResponse,
  positions: Map<string, { x: number; y: number }>,
): { nodes: WhyFlowNode[]; edges: Edge[] } {
  return {
    nodes: answer.nodes.map((n) => ({
      id: n.id,
      type: "why",
      position: positions.get(n.id) ?? { x: 0, y: 0 },
      data: { node: n, root: n.id === answer.root },
    })),
    edges: connectedEdges(answer).map((e, i) => ({
      id: `${e.from}->${e.to}#${i}`,
      source: e.to,
      target: e.from,
      type: "smoothstep",
      label: e.detail ? `${e.kind} · ${e.detail}` : e.kind,
      style: recordedEdge,
      markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14, color: "var(--primary)" },
    })),
  }
}

const whyNodeTypes = { why: WhyNodeView }

function WhyPanel({ objectType, objectKey }: { objectType: string; objectKey: string }) {
  const t = useT()
  const formatNumber = useFormatNumber()
  const scope = useScope()
  const why = useWhy(scope, objectType, objectKey)
  const [selected, setSelected] = useState<string | null>(null)
  const layout = useQuery({
    queryKey: ["layout:why", objectType, objectKey, why.dataUpdatedAt],
    enabled: why.data !== undefined,
    // Causes at the top, the object's current state at the bottom.
    queryFn: () =>
      why.data
        ? layeredLayout(
            why.data.nodes.map((n) => ({ id: n.id, width: WHY_NODE_WIDTH, height: WHY_NODE_HEIGHT })),
            why.data.edges.map((e, i) => ({ id: `${i}`, source: e.to, target: e.from })),
            "DOWN",
          )
        : new Map(),
    staleTime: Infinity,
  })
  const flow = useMemo(() => (why.data && layout.data ? whyFlow(why.data, layout.data) : null), [why.data, layout.data])

  if (why.isError)
    return <ErrorState error={why.error} what={t("objects.history")} onRetry={() => void why.refetch()} />
  if (why.isPending || !flow) {
    return (
      <div className="flex gap-6 p-6">
        <Skeleton className="h-[420px] flex-1" />
        <Skeleton className="h-[420px] w-96" />
      </div>
    )
  }
  if (why.data.nodes.length === 0) {
    return (
      <EmptyState icon={<FileQuestion />} title={t("objects.noRecordedHistory")}>
        <p>{t("objects.noRecordedHistoryDescription")}</p>
      </EmptyState>
    )
  }
  const selectedNode = why.data.nodes.find((n) => n.id === selected)
  // The cell returns correlated tasks in every state; only terminal failures await a decision.
  const failedTasks = (why.data.pendingCommandTasks ?? []).filter(
    (t) => isRecord(t) && (isRecord(t.values) ? t.values.status : t.status) === "failed",
  ).length
  const focused = selectedNode?.kind === "Commit" ? why.data.steps.filter((s) => s.commit === selectedNode.id) : []
  return (
    <div className="flex h-full min-h-0 flex-col overflow-auto border-t border-border-subtle lg:flex-row">
      <div className="relative min-h-80 min-w-0 flex-1">
        <GraphCanvas
          nodes={flow.nodes}
          edges={flow.edges}
          nodeTypes={whyNodeTypes}
          onNodeClick={(n) => setSelected(n.id)}
          minFitZoom={0.95}
        >
          <div className="absolute top-3 start-3 z-10">
            <TruthBadge kind="recorded" />
          </div>
        </GraphCanvas>
      </div>
      <aside
        className="max-h-80 w-full shrink-0 overflow-y-auto border-t border-border-subtle bg-sidebar lg:max-h-none lg:w-[400px] lg:border-t-0 lg:border-s"
        aria-label={t("objects.recordedSteps")}
      >
        <div className="flex items-center gap-2 border-b border-border-subtle px-4 py-3">
          <GitBranch className="size-3.5 text-subtle-foreground" aria-hidden />
          <h2 className="text-sm font-medium">{t("objects.steps")}</h2>
          <span className="text-xs text-subtle-foreground tabular">{formatNumber(why.data.steps.length)}</span>
        </div>
        {why.data.historyTruncated ? (
          <p className="mx-4 mt-3 text-xs text-muted-foreground">
            {t("objects.historyTruncated")}
          </p>
        ) : null}
        {why.data.deleted ? (
          <p className="mx-4 mt-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2 text-xs text-destructive">
            {why.data.deleted.summary}
          </p>
        ) : null}
        {failedTasks > 0 ? (
          <p className="mx-4 mt-3 rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-xs text-warning">
            {t("objects.failedCommands", { count: failedTasks })}{" "}
            <Link to="/$orgId/$environmentId/inbox" params={scope} className="underline">
              {t("objects.openInbox")}
            </Link>
          </p>
        ) : null}
        <ol className="relative px-4 py-3">
          {why.data.steps.map((step, i) => (
            <StepItem key={i} step={step} highlighted={focused.includes(step)} />
          ))}
        </ol>
      </aside>
    </div>
  )
}

function stepTitle(step: WhyStep, t: ReturnType<typeof useT>): string {
  switch (step.kind) {
    case "decision":
      return t("objects.stepDecision", { name: step.ruleId ?? step.ruleArtifactId ?? "" })
    case "action":
      return t("objects.stepAction", { name: step.actionId ?? "" })
    case "observation":
      return t("objects.stepObserved", { name: step.observation ?? "" })
    case "transitioned":
    case "observed_transition":
      return step.from && step.to
        ? t("objects.stepTransitionStates", { name: step.transition ?? "", from: step.from, to: step.to })
        : t("objects.stepTransition", { name: step.transition ?? "" })
    case "timer":
      return t(step.firedBy ? "objects.stepTimerScheduledFired" : "objects.stepTimerScheduled", {
        name: step.timerId ?? "",
      })
    case "timer_fired":
      return t("objects.stepTimerFired", { name: step.timerId ?? "" })
    case "command_enqueued":
      return t("objects.stepCommandEnqueued", { name: step.commandId ?? "" })
    case "command_result":
      return t("objects.stepCommandResult", { name: step.commandId ?? "" })
    default:
      // The contract admits future kinds as plain strings.
      return step.kind.replace(/_/g, " ")
  }
}

function StepItem({ step, highlighted }: { step: WhyStep; highlighted: boolean }) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const matched = asArray(step.matchedRows)
  return (
    <li className="relative border-s border-border-subtle pb-4 ps-4 last:pb-0">
      <span
        className={`absolute top-1.5 -start-[3.5px] size-1.5 rounded-full ${highlighted ? "bg-primary" : "bg-subtle-foreground/60"}`}
        aria-hidden
      />
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="w-full text-start">
        <div className="flex items-baseline gap-2">
          <span className="truncate text-sm text-foreground">{stepTitle(step, t)}</span>
          <span className="ms-auto shrink-0 font-mono text-2xs text-subtle-foreground tabular">
            {step.commit.replace(/^commit:/, "commit ")}
          </span>
        </div>
        {matched.length > 0 ? (
          <div className="mt-1 flex flex-wrap gap-1">
            {matched.map((m, j) => (
              <Badge key={j} tone="primary">
                {t("objects.matchedRow", {
                  row: isRecord(m)
                    ? (asString(m.rowId) ?? asString(m._id) ?? asString(m.id) ?? stringifyExact(m))
                    : String(m),
                })}
              </Badge>
            ))}
          </div>
        ) : null}
      </button>
      {open ? <JsonView value={step} className="mt-2 max-h-72" /> : null}
    </li>
  )
}
